import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextArea } from "../CommonUIComponents/TextArea/TextArea";
import { Microphone } from "../../assets/Icons/Microphone";
import { SendIcon } from "../../assets/Icons/SendIcon";
import { StopIcon } from "../../assets/Icons/StopIcon";
import SuperAgentSuggestions from "./SuperAgentSuggestions";
import { GlobeIcon } from "../../icons/globeIcon";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import "./SuperAgentChat.scss";
import {
  PLACEHOLDERS,
  ARIA_LABELS,
  CHAT_SUGGESTIONS,
  SUPER_REGIONS,
} from "../../util/constants";
import {
  setSelectedRegion,
  selectSelectedRegion,
} from "../../store/slices/regionSlice";

// Text input component with mic and send buttons for chat
const ChatInput = ({
  onSend,
  onChipClick,
  onSuggestionSelect,
  disabled = false,
  isLoading = false,
  onStop,
  editValue,
  onEditValueConsumed,
}) => {
  const dispatch = useDispatch();
  const selectedRegion = useSelector(selectSelectedRegion);

  // Dummy stop handler - to be implemented later
  const handleStop = () => {
    if (onStop) {
      onStop();
    }
  };
  const [inputValue, setInputValue] = useState("");
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef(null);

  // Populate input when an edit is requested from a user message bubble
  useEffect(() => {
    if (editValue != null && editValue !== "") {
      setInputValue(editValue);
      if (onEditValueConsumed) onEditValueConsumed();
    }
  }, [editValue, onEditValueConsumed]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(event.target)
      ) {
        setIsRegionDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Updates input value state on text change
  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  // Sends message and clears input field
  const handleSend = () => {
    if (disabled) return;
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (onSend) {
      onSend(trimmed);
    }
    setInputValue("");
  };

  // Handles Enter key to send message (Shift+Enter for new line)
  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  // Handles suggestion chip button clicks
  const handleChipClick = (text, mode) => {
    if (onChipClick) {
      onChipClick(text, mode);
    }
  };

  // Handles region selection
  const handleRegionSelect = (region) => {
    dispatch(setSelectedRegion(region));
    setIsRegionDropdownOpen(false);
  };

  return (
    <div className="super-agent-chat-input">
      <div className="super-agent-chat-input-row">
        {/* Knowledge Regions dropdown - HIDDEN for now but keeping code for future use.
           Default region "general" is still sent in API payload via selectedRegion state.*/}
        <div className="super-agent-region-selector" ref={regionDropdownRef}>
          <button
            className="super-agent-region-trigger"
            onClick={() => setIsRegionDropdownOpen((prev) => !prev)}
            aria-label="Select super region"
            type="button"
          >
            <GlobeIcon color="#737387" />
            <span className="super-agent-region-text">
              {selectedRegion?.name || "General"}
            </span>
            <NavDownArrow width={12} height={12} color="#ffffff" />
          </button>
          {isRegionDropdownOpen && (
            <div className="super-agent-region-dropdown">
              <div className="super-agent-region-dropdown-header">
                <GlobeIcon color="#737387" />
                <span>Knowledge Regions</span>
              </div>
              <div className="super-agent-region-dropdown-list">
                {SUPER_REGIONS.map((region) => (
                  <button
                    key={region.id}
                    type="button"
                    className={`super-agent-region-item ${
                      selectedRegion?.id === region.id ? "active" : ""
                    }`}
                    onClick={() => handleRegionSelect(region)}
                  >
                    <span>{region.name}</span>
                    {selectedRegion?.id === region.id && (
                      <span className="super-agent-region-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <TextArea
          className="super-agent-chat-textarea"
          placeholder={
            isLoading ? PLACEHOLDERS.PLEASE_WAIT : PLACEHOLDERS.ASK_ME_ANYTHING
          }
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <div className="super-agent-chat-input-icons">
          {isLoading ? (
            <button
              className="chatbot-icon-button2"
              aria-label="Stop response"
              onClick={handleStop}
            >
              <StopIcon />
            </button>
          ) : (
            <button
              className="chatbot-icon-button1"
              aria-label={ARIA_LABELS.SEND_MESSAGE}
              onClick={handleSend}
              disabled={disabled}
            >
              <SendIcon />
            </button>
          )}
        </div>
      </div>
      {onSuggestionSelect && (
        <SuperAgentSuggestions
          onSuggestionSelect={onSuggestionSelect}
          disabled={disabled}
        />
      )}
      {/* <div className="super-agent-chat-suggestions">
        <button
          className="super-agent-chat-chip"
          onClick={() =>
            handleChipClick(
              CHAT_SUGGESTIONS.MANDATORY_FIELDS,
              "mandatoryFieldsJE",
            )
          }
        >
          {CHAT_SUGGESTIONS.MANDATORY_FIELDS}
        </button>
      </div> */}
    </div>
  );
};

export default ChatInput;
